import { SettingsPermissions } from "@redotech/account-common/permissions";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import type { AddExchangeOrderTagsState } from "@redotech/redo-return-core-common/return-flow-step-valid";
import {
  StepId,
  StepTypeDetailsProps,
} from "@redotech/redo-return-ui/flows/step";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import { RedoTagInputField } from "@redotech/redo-web/arbiter-components/input/redo-tag-input-field";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { InfoTooltip } from "@redotech/redo-web/arbiter-components/tooltip/info-tooltip";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";

export const AddExchangeOrderTagsDetails = memo(
  function AddExchangeOrderTagsDetails({
    stepSelect,
    state,
    setState,
  }: StepTypeDetailsProps<AddExchangeOrderTagsState>) {
    const { t } = useTranslation();
    const canEditReturnsSettings = useHasPermission(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    );
    const [input, setInput] = useState("");

    const nextStepChange = useHandler((next: StepId | undefined) => {
      setState((state) => ({ ...state, next }));
    });

    const tags = useMemo(
      () => state.tags.map((name) => ({ text: name, color: "gray" as const })),
      [state.tags],
    );

    const handleTagsChange = useHandler((names: string[]) => {
      const unique: string[] = [];
      for (const name of names) {
        const trimmed = name.trim();
        if (trimmed && !unique.includes(trimmed)) {
          unique.push(trimmed);
        }
      }
      setState((state) => ({ ...state, tags: unique }));
    });

    return (
      <>
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {t("Exchange order tags")}
          </Text>
          <RedoTagInputField
            delimiters={["newline", "comma"]}
            inputValue={input}
            placeholder={t("Type a tag and press Enter")}
            setInputValue={setInput}
            setTags={handleTagsChange}
            state={canEditReturnsSettings ? "default" : "readonly"}
            tags={tags}
          />
          <Text fontSize="xxs" fontWeight="regular" textColor="secondary">
            {t(
              "Tags added to the exchange draft order when {{brandName}} creates it.",
              { brandName: "Redo" },
            )}
          </Text>
        </Flex>

        <Flex dir="column" gap="xs">
          <Flex align="center" dir="row" gap="xs">
            <RedoToggle
              disabled={!canEditReturnsSettings}
              label={t("Copy original order tags")}
              setValue={(value) =>
                setState((state) => ({
                  ...state,
                  copyOriginalOrderTags: value,
                }))
              }
              value={state.copyOriginalOrderTags ?? false}
            />
            <InfoTooltip
              boxSize={14}
              tooltip={{
                title: t(
                  "{{brandName}}-managed tags (such as exchange and hold tags) are never copied from the original order.",
                  { brandName: "Redo" },
                ),
              }}
            />
          </Flex>
          <Text fontSize="xxs" fontWeight="regular" textColor="secondary">
            {t(
              "Also copy tags from the original order onto the exchange order.",
            )}
          </Text>
        </Flex>

        <Divider />
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {t("Next step")}
          </Text>
          {stepSelect({ value: state.next, valueChange: nextStepChange })}
          {!state.next && (
            <Text fontSize="xs" textColor="error">
              {t("Required")}
            </Text>
          )}
        </Flex>
      </>
    );
  },
);
